import { z } from 'zod';
import { DEFAULT_GAME_OPTIONS, GameOptions } from '../types/game-options';

// Request-body and small response schemas for the ts-rest contract. Request
// bodies are validated at the ts-rest layer before a handler runs, so these
// encode the rules that were previously ad-hoc checks inside route handlers.

// Standardized error envelope. Replaces the old, inconsistent shapes
// (`{ error }` from the server error middleware vs `{ errorMessage }` in the
// client's ApiError vs `{ message }` read by Home.tsx).
export const ErrorBody = z.object({ error: z.string() });

// Generic acknowledgement for actions whose result the client reads from the
// subsequent websocket game-state broadcast rather than the HTTP response.
export const SuccessBody = z.object({ success: z.literal(true) });

export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const MeResponse = z.object({
  user: UserSchema,
  inGame: z.boolean(),
});

// Register returns the user plus, in development only, the raw auth token. This
// lets a single browser hold several distinct sessions by stashing each token in
// per-tab sessionStorage and sending it via header/query instead of the shared
// cookie (see the dev multi-session flow). In production the field is absent and
// the httpOnly cookie remains the only credential.
export const RegisterResponse = UserSchema.extend({
  token: z.string().optional(),
});

// A lobby row: structured fields the client formats itself, not a display string.
export const OpenGameSchema = z.object({
  id: z.string(),
  title: z.string(),
  code: z.string(),
  hostName: z.string(),
  /** Seats held by a human — computers and vacated seats excluded. */
  seatedCount: z.number(),
  totalSeats: z.number(),
  scoreToWin: z.number(),
  /** open = free seat pre-game; resume = paused game needs a seat; takeover = replace a computer. */
  joinKind: z.enum(['open', 'resume', 'takeover']),
});

export type OpenGame = z.infer<typeof OpenGameSchema>;

// name must be 1-20 chars (previously checked by hand in the /register handler).
export const RegisterBody = z.object({
  name: z.string().trim().min(1).max(20),
});

// The legal "score to win" options offered when creating a table.
export const SCORE_TO_WIN_OPTIONS = [7, 10, 11, 21] as const;

// Total players at a table: always teams of two, so 2, 3, or 4 teams.
export const PLAYER_COUNT_OPTIONS = [4, 6, 8] as const;

// The bids a player may make, lowest first. Pass (0) is always available and is
// not listed here. Single source of truth for the server's starting
// possibleBids, the MakeBidBody bound, and the client's bid buttons.
export const BID_OPTIONS = [2, 3, 4] as const;

export const TABLE_TITLE_MAX_LENGTH = 28;

// The host's rule variants. Every field defaults, so a client that posts no
// `options` (or an older one that doesn't know about a newer rule) still gets a
// valid, standard game.
export const GameOptionsSchema = z.object({
  nonBiddingTeamsScore: z.boolean().default(DEFAULT_GAME_OPTIONS.nonBiddingTeamsScore),
  stickTheDealer: z.boolean().default(DEFAULT_GAME_OPTIONS.stickTheDealer),
}) satisfies z.ZodType<GameOptions, z.ZodTypeDef, unknown>;

// Table configuration chosen by the host at creation. All fields default so an
// empty POST (older clients / tests) still produces a valid public 11-point,
// 4-player game. An omitted or blank title becomes "<host>'s Table" server-side.
export const OpenTableBody = z.object({
  title: z.string().trim().max(TABLE_TITLE_MAX_LENGTH).optional(),
  scoreToWin: z.union([z.literal(7), z.literal(10), z.literal(11), z.literal(21)]).default(11),
  isPrivate: z.boolean().default(false),
  numberOfPlayers: z.union([z.literal(4), z.literal(6), z.literal(8)]).default(4),
  options: GameOptionsSchema.default({}),
});

export type OpenTableConfig = z.infer<typeof OpenTableBody>;

/** The config an untouched "just open a table" request produces. */
export const defaultTableConfig = (): OpenTableConfig => OpenTableBody.parse({});

// Score to win is now fixed on the table at creation; starting chooses whether
// to fill empty seats with computers, and whether to lock the table. An unlocked
// table with a computer seat stays joinable from the lobby (a newcomer can take
// over a computer); locking closes it to new players. `locked` defaults to false
// so older clients / tests that send only `computers` still start unlocked.
export const StartGameBody = z.object({
  computers: z.boolean(),
  locked: z.boolean().default(false),
});

// Setback bids are pass (0) or 2-4; 1 is not a legal bid.
export const MakeBidBody = z.object({
  bid: z
    .number()
    .int()
    .min(0)
    .max(Math.max(...BID_OPTIONS)),
});

export const PlayCardBody = z.object({
  cardId: z.string(),
});

// Move the caller to a different seat (and therefore team) before the game
// starts. Seats are 0-7 (tables of 4/6/8); the handler rejects the move if the
// seat is taken or out of range for the table.
export const ChangeSeatBody = z.object({
  seatIndex: z.number().int().min(0).max(7),
});

export const TableIdParams = z.object({
  id: z.string(),
});

// Join via a table's short invite code (used by invite links).
export const TableCodeParams = z.object({
  code: z.string(),
});

// Fill a vacant seat (left behind when a player leaves mid-game) with a
// computer. Seats are 0-3; the handler rejects it if the seat is not vacant or
// the caller is not the host.
export const FillSeatBody = z.object({
  seatIndex: z.number().int().min(0).max(7),
});

// Host-only: lock/unlock the table. Locking hides it from the lobby and makes
// the invite code admit spectators instead of seating new players.
export const SetTableLockBody = z.object({
  locked: z.boolean(),
});
